import {
  useEffect,
  useRef,
  type KeyboardEvent,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';

import './calendar.css';

/**
 * Calendrier de jours (semaine ou mois, lundi → dimanche), repris de l'app Protection Civile 34
 * (`OasisWeekCalendar`, `MonthCalendarView`) et du calendrier de `pc-availabilities` : chaque
 * jour est un bouton (nombre du jour + contenu propre à l'écran : créneaux, effectifs), le jour
 * courant est souligné en orange, le jour choisi en bleu. Sept colonnes qui tiennent dans
 * 360 px (aucun défilement horizontal). Les dates sont des chaînes `AAAA-MM-JJ` (jour civil,
 * sans fuseau).
 */

const pad = (n: number): string => String(n).padStart(2, '0');

/** `AAAA-MM-JJ` d'une date locale. */
export const isoDay = (d: Date): string =>
  `${String(d.getFullYear())}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** Date locale à midi (évite les sauts d'heure d'été). */
export const parseIsoDay = (day: string): Date => new Date(`${day}T12:00:00`);

export const addDays = (day: string, n: number): string => {
  const d = parseIsoDay(day);
  d.setDate(d.getDate() + n);
  return isoDay(d);
};

/** Rang du jour dans la semaine française : lundi 0 … dimanche 6. */
export const weekdayIndex = (day: string): number => (parseIsoDay(day).getDay() + 6) % 7;

/** Lundi de la semaine du jour. */
export const mondayOf = (day: string): string => addDays(day, -weekdayIndex(day));

/** Les sept jours de la semaine commençant au lundi donné. */
export const weekOf = (monday: string): string[] =>
  Array.from({ length: 7 }, (_, i) => addDays(monday, i));

/** Tous les jours du mois `AAAA-MM`. */
export const daysOfMonth = (month: string): string[] =>
  Array.from({ length: 31 }, (_, i) => addDays(`${month}-01`, i)).filter((d) =>
    d.startsWith(month),
  );

/** Mois `AAAA-MM` décalé de `delta` mois. */
export const shiftMonth = (month: string, delta: number): string => {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return isoDay(new Date(y, m - 1 + delta, 1)).slice(0, 7);
};

const WEEKDAYS = ['lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.', 'dim.'];

export interface CalendarProps {
  /** Jours affichés, dans l'ordre (une semaine ou un mois). */
  days: string[];
  /** Nom de la liste pour les lecteurs d'écran (« Disponibilités de novembre 2026 »). */
  label: string;
  /** Jour courant (`AAAA-MM-JJ`). */
  today: string;
  /** Jour choisi : bouton pressé. */
  selected?: string | undefined;
  onSelect: (day: string) => void;
  /** Nom accessible du bouton d'un jour (date en toutes lettres et état). */
  dayLabel: (day: string) => string;
  /** Contenu du jour sous son numéro (pastilles de créneaux, effectifs). */
  renderDay?: ((day: string) => ReactNode) | undefined;
  /** Classe supplémentaire d'un jour (`o-cal__day--absent`…). */
  dayClass?: ((day: string) => string | undefined) | undefined;
  /** Jours passés atténués. */
  dimPast?: boolean | undefined;
  /** `week` : cases plus hautes, contenu en colonne. */
  variant?: 'month' | 'week' | undefined;
  /** Classe supplémentaire du calendrier. */
  className?: string | undefined;
  /**
   * Sélection de plusieurs jours (facultatif) : les jours de l'ensemble sont « pressés » ;
   * `selected` n'est plus alors que le jour courant du clavier (curseur).
   */
  chosen?: ReadonlySet<string> | undefined;
  /** Clic ou Entrée / Espace sur un jour, à la place de `onSelect` (sélection multiple). */
  onActivate?: ((day: string, e: MouseEvent<HTMLButtonElement>) => void) | undefined;
  /** Après un déplacement au clavier (`from` → `to`), par exemple Maj + flèche pour étendre. */
  onKeyMove?: ((from: string, to: string, e: KeyboardEvent<HTMLButtonElement>) => void) | undefined;
}

/**
 * Jour visé par une touche du calendrier (motif « grille » de l'APG WAI-ARIA) : flèches
 * gauche / droite = jour précédent / suivant, haut / bas = semaine précédente / suivante,
 * Début / Fin = lundi / dimanche de la semaine. `null` pour une autre touche.
 */
export const dayForKey = (day: string, key: string): string | null => {
  switch (key) {
    case 'ArrowLeft':
      return addDays(day, -1);
    case 'ArrowRight':
      return addDays(day, 1);
    case 'ArrowUp':
      return addDays(day, -7);
    case 'ArrowDown':
      return addDays(day, 7);
    case 'Home':
      return mondayOf(day);
    case 'End':
      return addDays(mondayOf(day), 6);
    default:
      return null;
  }
};

/** Cases vides avant le premier jour pour l'aligner sous son jour de semaine. */
export const leadingBlanks = (days: string[]): number =>
  days[0] === undefined ? 0 : weekdayIndex(days[0]);

export function Calendar({
  days,
  label,
  today,
  selected,
  onSelect,
  dayLabel,
  renderDay,
  dayClass,
  dimPast = false,
  variant = 'month',
  className,
  chosen,
  onActivate,
  onKeyMove,
}: CalendarProps): ReactElement {
  const lead = leadingBlanks(days);
  const list = useRef<HTMLUListElement>(null);
  const focusNext = useRef<string | null>(null);
  // Un seul jour dans l'ordre de tabulation (le jour choisi, sinon aujourd'hui, sinon le
  // premier) ; les flèches choisissent le jour voisin et y portent le focus, même quand la
  // période affichée change.
  const tabbable =
    selected !== undefined && days.includes(selected)
      ? selected
      : days.includes(today)
        ? today
        : days[0];
  useEffect(() => {
    if (focusNext.current === null || focusNext.current !== selected) return;
    const target = list.current?.querySelector<HTMLButtonElement>(
      `[data-day="${focusNext.current}"]`,
    );
    if (target !== null && target !== undefined) {
      target.focus();
      focusNext.current = null;
    }
  });
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, day: string): void => {
    const next = dayForKey(day, e.key);
    if (next === null) return;
    e.preventDefault();
    focusNext.current = next;
    onSelect(next);
    onKeyMove?.(day, next, e);
  };
  return (
    <div className={`o-cal o-cal--${variant}${className === undefined ? '' : ` ${className}`}`}>
      <div className="o-cal__grid" aria-hidden="true">
        {WEEKDAYS.map((w) => (
          <span key={w} className="o-cal__weekday">
            {w}
          </span>
        ))}
      </div>
      <ul ref={list} className="o-cal__grid o-list-reset" aria-label={label}>
        {Array.from({ length: lead }, (_, i) => (
          <li key={`vide-${String(i)}`} aria-hidden="true" />
        ))}
        {days.map((d) => {
          const classes = ['o-cal__day'];
          if (d === today) classes.push('o-cal__day--today');
          if (d === selected) classes.push('o-cal__day--selected');
          if (chosen?.has(d) === true) classes.push('o-cal__day--chosen');
          if (dimPast && d < today) classes.push('o-cal__day--past');
          const extra = dayClass?.(d);
          if (extra !== undefined) classes.push(extra);
          return (
            <li key={d}>
              <button
                type="button"
                className={classes.join(' ')}
                aria-pressed={chosen === undefined ? d === selected : chosen.has(d)}
                aria-current={d === today ? 'date' : undefined}
                data-day={d}
                tabIndex={d === tabbable ? 0 : -1}
                onKeyDown={(e) => {
                  onKeyDown(e, d);
                }}
                onClick={(e) => {
                  if (onActivate === undefined) onSelect(d);
                  else onActivate(d, e);
                }}
              >
                <span className="o-visually-hidden">{dayLabel(d)}</span>
                <span className="o-cal__number" aria-hidden="true">
                  {parseIsoDay(d).getDate()}
                </span>
                {renderDay !== undefined && (
                  <span className="o-cal__content" aria-hidden="true">
                    {renderDay(d)}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
